import React from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import SeoHead from "../components/SeoHead";

const intendedUse = [
  "Software testing",
  "Quality assurance",
  "User acceptance testing",
  "Account verification testing",
  "Email workflow validation",
  "Privacy protection",
];

const notIntended = [
  "Spam",
  "Fraud",
  "Phishing",
  "Illegal activity",
  "Circumventing platform security",
];

function About() {
  return (
    <Layout>
      <SeoHead
        title="About Ghost Inbox – Privacy-Focused Temporary Inbox Platform"
        description="Ghost Inbox helps developers, QA engineers, and privacy-conscious users create secure temporary inboxes for software testing, validation workflows, and spam protection."
        path="/about"
      />

      <div className="mx-auto max-w-3xl px-6 py-16">
        <p className="text-xs font-medium uppercase tracking-wider text-emerald-400">Company</p>
        <h1 className="mt-2 text-3xl font-bold text-white">About Us</h1>
        <p className="mt-4 leading-relaxed text-zinc-400">
          Ghost Inbox is a privacy-focused temporary inbox platform built for legitimate
          professional and personal use cases — not bulk abuse or anonymous misuse.
        </p>

        <section className="mt-12 rounded-xl border border-zinc-800 bg-zinc-900/40 p-6">
          <h2 className="text-lg font-semibold text-white">Our mission</h2>
          <p className="mt-3 text-sm leading-relaxed text-zinc-400">
            Ghost Inbox helps developers, testers, and privacy-conscious users create temporary
            inboxes for legitimate testing and spam protection purposes. We provide a secure,
            account-linked environment where teams can validate email workflows without exposing
            personal inboxes or maintaining permanent test mailboxes.
          </p>
        </section>

        <section className="mt-6 rounded-xl border border-zinc-800 bg-zinc-900/40 p-6">
          <h2 className="text-lg font-semibold text-white">Intended use cases</h2>
          <p className="mt-3 text-sm text-zinc-400">
            Ghost Inbox is designed for professional and responsible use, including:
          </p>
          <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-zinc-400">
            {intendedUse.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="mt-6 rounded-xl border border-zinc-800 bg-zinc-900/40 p-6">
          <h2 className="text-lg font-semibold text-white">Not intended for</h2>
          <p className="mt-3 text-sm text-zinc-400">
            To protect platform integrity and our users, Ghost Inbox must not be used for:
          </p>
          <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-zinc-400">
            {notIntended.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p className="mt-4 text-sm text-zinc-400">
            For full details, see our{" "}
            <Link to="/acceptable-use" className="text-emerald-400 hover:underline">
              Acceptable Use Policy
            </Link>{" "}
            and{" "}
            <Link to="/terms" className="text-emerald-400 hover:underline">
              Terms of Service
            </Link>
            .
          </p>
        </section>

        <section className="mt-6 rounded-xl border border-zinc-800 bg-zinc-900/40 p-6">
          <h2 className="text-lg font-semibold text-white">Built for trust</h2>
          <p className="mt-3 text-sm leading-relaxed text-zinc-400">
            Every inbox is tied to a registered account, messages expire automatically after a
            defined retention period, and our infrastructure is designed for reliability and
            security. We continuously invest in abuse prevention, transparent policies, and a
            professional product experience for teams who depend on email workflow testing.
          </p>
        </section>

        <div className="mt-12 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-zinc-400">
            Ready to get started?{" "}
            <Link to="/signup" className="text-emerald-400 hover:underline">
              Create a free account
            </Link>
            .
          </p>
          <Link
            to="/contact"
            className="inline-block rounded-lg border border-zinc-700 px-5 py-2.5 text-center text-sm text-zinc-300 hover:border-zinc-500"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </Layout>
  );
}

export default About;
